import { ChangeEvent, useCallback, useMemo } from 'react';
import { useLang } from 'hooks/lang';

import { calculateServiceCost } from 'entities';

import { Column, Row, Label } from 'components/Blocks';
import { VariableCostInput } from 'components/Inputs';

import { sortByName } from 'utils/array';
import { formatCurrency } from 'utils/text';
import { getCountryLabel } from 'utils/countries';

import { CreateProjectDispatchAction, ProjectCreationProps } from '../types';

interface CostsProps {
  state: ProjectCreationProps;
  dispatch: CreateProjectDispatchAction;
}

export function CostsControl({ state, dispatch }: CostsProps): JSX.Element {
  const { countries, services } = state;

  const { t } = useLang();

  const updateServiceCost = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = target;

      const [serviceId, country, property, index] = name.split('#');

      dispatch({
        services: ({ services }) =>
          services.map((service) => {
            if (service.id !== serviceId) return service;

            const withCosts = {
              ...service,
              costs: {
                ...service.costs,
                [country]: service.costs[country].map((cost, costIndex) =>
                  costIndex !== Number(index) ? cost : { ...cost, [property]: value },
                ),
              },
            };

            if (!withCosts.outsourceConfig) return withCosts;

            return {
              ...withCosts,

              outsourceConfig: {
                ...withCosts.outsourceConfig,
                clientCost: {
                  ...withCosts.outsourceConfig.clientCost,
                  value: calculateServiceCost(withCosts),
                },
              },
            };
          }),
      });
    },
    [dispatch],
  );

  const servicesByCountry = useMemo(() => {
    return Object.fromEntries(
      countries.map((country) => {
        const countryServices = services.map(({ costs, ...service }) => ({
          ...service,
          cost: costs[country],
        }));

        return [country, sortByName(countryServices)];
      }),
    );
  }, [countries, services]);

  return (
    <Column>
      <Label className="text-lg">{t('costs')}</Label>

      {!services.length || !countries.length ? (
        <p>{t('select-services-and-their-countries-to-enable-costs')}</p>
      ) : (
        <Column>
          {countries
            .slice()
            .sort()
            .map((country) => {
              return (
                <Column className="rounded-md shadow-md" key={`${country} -- card`} gap="0">
                  <div className="rounded-t-md bg-muted px-5 py-3 font-medium text-primary">
                    {getCountryLabel(country)}
                  </div>

                  <Column gap="0">
                    {servicesByCountry[country].map((service) => (
                      <Column className="border-t p-5" key={`cost--${service.id}--${country}`}>
                        <Row justifyContent="space-between">
                          <span className="font-medium text-primary">{service.name}</span>

                          <span className="font-medium text-primary">
                            {formatCurrency(
                              calculateServiceCost({
                                ...service,
                                costs: { [state.currency]: service.cost },
                              }),
                              state.currency,
                            )}
                          </span>
                        </Row>

                        <div className="grid w-full grid-cols-3 gap-4 small_tablet:grid-cols-1">
                          {service.cost.map((cost, index) => (
                            <VariableCostInput
                              key={`cost-${country}-${cost.name}`}
                              type={cost.type}
                              label={cost.name}
                              onChange={updateServiceCost}
                              name={`${service.id}#${country}#value#${index}`}
                              currency={cost.currency}
                              currencyDisabled
                              value={cost.value || ''}
                            />
                          ))}
                        </div>
                      </Column>
                    ))}
                  </Column>
                </Column>
              );
            })}
        </Column>
      )}
    </Column>
  );
}
